import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { boardApi } from '@/api/endpoints';
import Avatar from '@/components/common/Avatar';
import Pagination from '@/components/common/Pagination';
import { Empty, ErrorState, Loading } from '@/components/common/State';
import { POST_STATUS_LABEL, formatDate } from '@/utils/format';
import { useAuthStore } from '@/store/authStore';

export default function BoardListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);

  const boardType = searchParams.get('boardType') ?? 'QNA';
  const page = Number(searchParams.get('page') ?? 1);
  const keyword = searchParams.get('keyword') ?? '';

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['boards', boardType, page, keyword],
    queryFn: () => boardApi.list({ boardType, page, size: 15, keyword: keyword || undefined }),
  });

  const patch = (next: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => (v ? params.set(k, v) : params.delete(k)));
    if (!('page' in next)) params.delete('page');
    setSearchParams(params);
  };

  return (
    <>
      <h1 className="panel-title">{boardType === 'QNA' ? '상품 문의' : '고객의 소리 (VoC)'}</h1>

      <div className="tabs">
        <button
          type="button"
          className={boardType === 'QNA' ? 'is-active' : undefined}
          onClick={() => patch({ boardType: 'QNA' })}
        >
          상품문의
        </button>
        <button
          type="button"
          className={boardType === 'VOC' ? 'is-active' : undefined}
          onClick={() => patch({ boardType: 'VOC' })}
        >
          고객의 소리
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, justifyContent: 'space-between' }}>
        <input
          className="input"
          style={{ maxWidth: 260 }}
          placeholder="제목/내용 검색"
          defaultValue={keyword}
          onKeyDown={(e) => {
            if (e.key === 'Enter') patch({ keyword: e.currentTarget.value });
          }}
        />
        {user && (
          <Link to={`/boards/write?boardType=${boardType}`} className="btn btn--primary btn--sm">
            글쓰기
          </Link>
        )}
      </div>

      {isLoading && <Loading />}
      {isError && <ErrorState error={error} onRetry={refetch} />}

      {data &&
        (data.content.length === 0 ? (
          <Empty title="등록된 글이 없습니다." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>유형</th>
                    <th>제목</th>
                    <th>작성자</th>
                    <th>상태</th>
                    <th>조회</th>
                    <th>작성일</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((post) => (
                    <tr key={post.postId}>
                      <td>{post.category ?? '-'}</td>
                      <td className="wrap">
                        <Link to={`/boards/${post.postId}?boardType=${boardType}`}>
                          {post.secret && '🔒 '}
                          {post.title}
                        </Link>
                        {post.commentCount > 0 && (
                          <span className="muted"> [{post.commentCount}]</span>
                        )}
                      </td>
                      <td>
                        <span className="writer">
                          <Avatar src={post.writerProfileImageUrl} size={20} />
                          {post.writer}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge badge--${post.status === 'ANSWERED' ? 'success' : 'muted'}`}
                        >
                          {POST_STATUS_LABEL[post.status]}
                        </span>
                      </td>
                      <td>{post.viewCount}</td>
                      <td>{formatDate(post.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              onChange={(p) => patch({ page: String(p) })}
            />
          </>
        ))}
    </>
  );
}
